<script setup lang="ts">
import { useRoute, useRouter, computed, ref, watch } from '#imports'

export interface SaferGamblingSearchProps {
    label?: string
    noResultsText?: string
    placeholder?: string
    queryParam?: string
}

const props = withDefaults(defineProps<SaferGamblingSearchProps>(), {
    label: 'Search questions and answers',
    noResultsText: 'No results found',
    placeholder: 'What are you looking for?',
    queryParam: 'search'
})

const router = useRouter()
const route = useRoute()

const { searchQuery, searchMappedResults, isLoading } = useSaferGamblingSearch()
const { query, queryBySlug } = useLayeredContent()

const selectedResultId = computed(() => route.query[props.queryParam] as string | undefined)
const isModalOpen = computed(() => Boolean(selectedResultId.value))

const selectedQa = ref<Record<string, unknown> | null>(null)

const qaHeader = computed(() => selectedQa.value?.title as string | undefined)
const qaVideoId = computed(() => selectedQa.value?.video_id as string | undefined)
const qaCtas = computed(() => selectedQa.value?.ctas as { label: string, url: string, variant?: 'primary' | 'secondary' }[] | undefined)
const qaExpandableTextBlocks = computed(() => selectedQa.value?.expandableTextBlocks as { title: string, content: any }[] | undefined)

async function fetchSelectedQa() {
    if (!selectedResultId.value) {
        selectedQa.value = null
        return
    }

    const value = selectedResultId.value.split('#')[0]
    if (!value) {
        selectedQa.value = null
        return
    }
    const result = value.includes('/')
        ? await query('safer-gambling/qas', value)
        : await queryBySlug('safer-gambling/qas', value)

    selectedQa.value = result
}

if (import.meta.client) {
    watch(selectedResultId, () => fetchSelectedQa(), { immediate: true })
}

function onSearchSelect(result: { id?: string, title?: string }) {
    if (result.id) {
        router.push({ query: { ...route.query, [props.queryParam]: result.id } })
        searchQuery.value = ''
    }
}

function closeModal() {
    const query = { ...route.query }
    delete query[props.queryParam]
    router.push({ query })
}
</script>

<template>
    <div>
        <UISearch
            v-model="searchQuery"
            class="search"
            :items="searchMappedResults"
            :label="label"
            :placeholder="placeholder"
            :no-results-text="noResultsText"
            :loading="isLoading"
            @select-result="onSearchSelect"
        />

        <UIDialog
            v-if="isModalOpen"
            :model-value="true"
            :header="qaHeader"
            :show-footer="false"
            :scrollable="true"
            :wide="true"
            class="qa-modal"
            @close="closeModal"
        >
            <SaferGamblingQaContent
                v-if="selectedQa"
                :video-id="qaVideoId"
                :body="selectedQa.body"
                :ctas="qaCtas"
                :expandable-text-blocks="qaExpandableTextBlocks"
            />
            <UILoader v-else />
        </UIDialog>
    </div>
</template>

<style lang="scss" scoped>
:deep(.search) {
    .icon--search {
        width: auto !important;
        height: 20px;
        transform: translateX(-4px);
    }
}
</style>

<style lang="scss">
.qa-modal {
    .dialog__header {
        display: flex;
        align-items: center;

        h2 {
            position: relative;
            font-size: $font-size-lg;
            color: $brand-primary;
            font-weight: $text-bold;
            padding: 8px 10px;
        }

        .dialog__header-bottom {
            flex-grow: 1;
        }

        .dialog__close {
            position: relative;
            margin: auto 0;
            bottom: 0;
        }
    }

    .dialog__body {
        color: $dialog-font-color;
        font-size: $font-size-base;
        padding: 20px 20px 100px;

        @include tablet(md) {
            padding: 20px 30px;
        }

        .btn {
            margin-top: 15px;
            height: 50px;
            align-items: center;
            display: flex;

            @include tablet(md) {
                margin-top: 30px;
            }
        }
    }

    .video {
        margin-bottom: 30px;
    }
}
</style>
